import { useMedplum } from "@medplum/react-hooks";
import { chatThreadQueries } from "@repo/agent-chat";
import type { PatientDto } from "@repo/data-store";
import { usePractitioners } from "@repo/data-store";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@repo/ui/components/ui/breadcrumb";
import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@repo/ui/components/ui/command";
import { Kbd } from "@repo/ui/components/ui/kbd";
import { formatForDisplay } from "@tanstack/react-hotkeys";
import { useQuery } from "@tanstack/react-query";
import { dispatchCommand } from "@/app/command-palette/dispatch-command";
import { categoryLabels, groupedCommands } from "@/app/command-palette/registry";
import type { CommandContext, CommandId } from "@/app/command-palette/types";
import { useCommandPalette } from "@/app/command-palette/use-command-palette";
import { useResolvedHotkey } from "@/app/command-palette/use-hotkey-store";
import { openAgent, setActiveThreadId } from "@/features/chat";
import { ChatHistorySearchList } from "@/features/chat/components/chat-history-search-list";
import { ringColleague } from "@/features/conferencing";
import { practitionerFhirIdFromProfile } from "@/shared/hooks/use-current-practitioner";
import { AI_ENGINE_URL } from "@/shared/lib/ai-engine-url";
import { PatientSearchList } from "@/shared/pickers/patient-search-list";
import { PractitionerSearchList } from "@/shared/pickers/practitioner-search-list";

function CommandHotkeyDisplay({ commandId }: { commandId: CommandId }) {
  const hotkey = useResolvedHotkey(commandId);
  if (!hotkey) return null;
  return <CommandShortcut>{formatForDisplay(hotkey)}</CommandShortcut>;
}

interface CommandPaletteProps {
  patients: PatientDto[];
  commandCtx: CommandContext;
}

export function CommandPalette({ patients, commandCtx }: CommandPaletteProps) {
  const { open, view, searchValue, setSearchValue, navigate, handleNavigateToView, handleOpenChange, placeholder } =
    useCommandPalette();
  const medplum = useMedplum();
  const { data: threads = [] } = useQuery(chatThreadQueries.list(medplum, AI_ENGINE_URL));

  /**
   * Everyone but the caller. `startCall` refuses a self-call outright, so this
   * is not the guard — it is the reason the clinician never sees a row that
   * cannot do anything.
   */
  const { practitioners } = usePractitioners();
  // The FHIR row locator, not the domain id: `fhirLocator` is a literal
  // `Practitioner/<row id>` and the domain identity matches none of them. The
  // helper also refuses a non-Practitioner profile, which a hand-built string
  // would happily turn into somebody else's locator.
  const meFhirId = practitionerFhirIdFromProfile(medplum.getProfile());
  const meLocator = meFhirId ? `Practitioner/${meFhirId}` : undefined;
  const colleagues = practitioners.filter((p) => p.fhirLocator !== meLocator);

  return (
    <CommandDialog open={open} onOpenChange={handleOpenChange} className="sm:max-w-lg">
      <div className="px-3 py-2 flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            {view === "home" ? (
              <BreadcrumbItem>
                <BreadcrumbPage>Command Palette</BreadcrumbPage>
              </BreadcrumbItem>
            ) : (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink onClick={() => handleNavigateToView("home")} className="cursor-pointer">
                    Command Palette
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    {view === "patient-search"
                      ? "Search Patients"
                      : view === "colleague-search"
                        ? "Call a Colleague"
                        : "Search Chat History"}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Kbd>ESC</Kbd>
          <span>{view === "home" ? "to close" : "to go back"}</span>
        </div>
      </div>
      <CommandInput placeholder={placeholder} value={searchValue} onValueChange={setSearchValue} />
      <CommandList>
        {view === "home" &&
          (Object.keys(categoryLabels) as Array<keyof typeof categoryLabels>).map((cat) => (
            <CommandGroup key={cat} heading={categoryLabels[cat]}>
              {groupedCommands[cat].map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  value={[cmd.label, ...(cmd.keywords ?? [])].join(" ")}
                  onSelect={() =>
                    dispatchCommand(cmd, {
                      navigate,
                      setPaletteView: handleNavigateToView,
                      closePalette: () => handleOpenChange(false),
                      ctx: commandCtx,
                    })
                  }
                >
                  <cmd.icon />
                  <span>{cmd.label}</span>
                  <CommandHotkeyDisplay commandId={cmd.id} />
                </CommandItem>
              ))}
            </CommandGroup>
          ))}

        {view === "patient-search" && (
          <PatientSearchList
            patients={patients}
            onSelect={(patientId) => {
              handleOpenChange(false);
              navigate({
                to: "/patients/$patientId",
                params: { patientId },
              });
            }}
          />
        )}

        {view === "colleague-search" && (
          <PractitionerSearchList
            practitioners={colleagues}
            onSelect={(practitionerLocator) => {
              // No confirm step: selecting IS the call. A ring is undone by the
              // colleague declining or by nobody answering, and the palette has
              // no other command whose result a dialog would guard.
              handleOpenChange(false);
              void ringColleague(practitionerLocator);
            }}
          />
        )}

        {view === "chat-history" && (
          <ChatHistorySearchList
            threads={threads}
            onSelect={(threadId) => {
              setActiveThreadId(threadId);
              openAgent();
              handleOpenChange(false);
            }}
          />
        )}
      </CommandList>
    </CommandDialog>
  );
}
